import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import { useBlogPosts } from './hooks/useBlogPosts';
import { HelmetProvider } from 'react-helmet-async';
import SEO from './components/SEO';
import BlogCarousel from './components/BlogCarousel';
import MainNavigation from './components/layout/MainNavigation';
import Footer from './components/layout/Footer';
import HeroSection from './components/layout/HeroSection';
import CookieConsentBanner from './components/layout/CookieConsentBanner';
import WhatsAppButton from './components/layout/WhatsAppButton';
import ServicesSection from './components/layout/ServicesSection';
import AboutSection from './components/layout/AboutSection';
import AboutExtrasSection from './components/layout/AboutExtrasSection';
import TestimonialsSection from './components/layout/TestimonialsSection';
import CtaSection from './components/layout/CtaSection';
import { Analytics } from '@vercel/analytics/react';
import { whatsappHref, registrarCliqueWhatsApp, WHATSAPP_CELULAR } from './lib/origem';
// Declaração de tipos para o GTM e Cal.com
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    gtag: (...args: unknown[]) => void;
    __gtmLoaded?: boolean;
    Cal?: ((...args: unknown[]) => void) & {
      q?: unknown[][];
      ns?: Record<string, unknown>;
      loaded?: boolean;
    };
  }
}

// Importações dinâmicas das imagens
import heroImageDesktop from './assets/img/img-hero.webp';
import heroImageMobile from './assets/img/img-hero-M.webp';
import logoImage from './assets/img/logo-branco.png';
import sobreImage from './assets/img/Sobre.webp';
import pecaImage from './assets/img/peca.webp';
import blogImage from './assets/img/blog.webp';

// Importações das páginas
import Privacidade from './pages/Privacidade';
import Termos from './pages/Termos';
import Blog from './pages/blog/index';
import PostDetail from './pages/blog/PostDetail';
import NaoEncontrada from './pages/NaoEncontrada';

const GTM_ID = 'GTM-TH7HM2Z2';
const COOKIE_CONSENT_KEY = 'cookieConsent';

const ensureDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag(...args: unknown[]) {
      window.dataLayer.push(args as unknown as Record<string, unknown>);
    };
};

const updateConsentMode = (granted: boolean) => {
  ensureDataLayer();
  window.gtag('consent', 'update', {
    ad_storage: granted ? 'granted' : 'denied',
    ad_user_data: granted ? 'granted' : 'denied',
    ad_personalization: granted ? 'granted' : 'denied',
    analytics_storage: granted ? 'granted' : 'denied',
  });
};

const loadGtm = () => {
  if (window.__gtmLoaded) {
    return;
  }

  if (document.getElementById('gtm-script')) {
    window.__gtmLoaded = true;
    return;
  }

  ensureDataLayer();
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  });

  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  const firstScript = document.getElementsByTagName('script')[0];
  const script = document.createElement('script');
  script.id = 'gtm-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  firstScript?.parentNode?.insertBefore(script, firstScript);
  window.__gtmLoaded = true;
};


// Componente para rastrear mudanças de rota
function RouteTracker() {
  const location = useLocation();

  useEffect(() => {
    if (window.__gtmLoaded && window.dataLayer) {
      window.dataLayer.push({
        event: 'page_view',
        page_path: location.pathname
      });
    }
  }, [location]);

  return null;
}

// Componente principal que envolve as rotas
function AppRoutes() {
  return (
    <>
      <RouteTracker />
      <Routes>
        <Route path="/" element={<MainContent />} />
        <Route path="/privacidade" element={<Privacidade />} />
        <Route path="/termos" element={<Termos />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<PostDetail />} />
        <Route path="*" element={<NaoEncontrada />} />
      </Routes>
    </>
  );
}

// Componente para o conteúdo principal
function MainContent() {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [contactTab, setContactTab] = useState<'agendar' | 'contato'>('agendar');
  const [formStatus, setFormStatus] = React.useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [scrollPosition, setScrollPosition] = useState(0);
  const [formErrors, setFormErrors] = useState({
    name: '',
    whatsapp: '',
    email: '',
    message: '',
  });
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    message: '',
    whatsapp: ''
  });

  const formatWhatsApp = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);

    if (digits.length <= 2) {
      return digits;
    }

    if (digits.length <= 6) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    }

    if (digits.length <= 10) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }

    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const validateForm = (data: typeof formData) => {
    const errors = {
      name: '',
      whatsapp: '',
      email: '',
      message: '',
    };

    if (data.name.trim().length < 2) {
      errors.name = 'Digite seu nome completo.';
    }

    const whatsappDigits = data.whatsapp.replace(/\D/g, '');
    if (whatsappDigits.length < 10 || whatsappDigits.length > 11) {
      errors.whatsapp = 'Informe um WhatsApp válido com DDD.';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email.trim())) {
      errors.email = 'Digite um e-mail válido.';
    }

    if (data.message.trim().length < 15) {
      errors.message = 'Conte um pouco mais para eu compreender melhor sua necessidade.';
    }

    return errors;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const normalizedValue = name === 'whatsapp' ? formatWhatsApp(value) : value;

    setFormData(prev => ({
      ...prev,
      [name]: normalizedValue
    }));

    setFormErrors(prev => ({
      ...prev,
      [name]: ''
    }));

    if (formStatus !== 'idle') {
      setFormStatus('idle');
    }
  };

  const { posts: blogPosts } = useBlogPosts();

  const handleScheduleClick = () => {
    setContactTab('agendar');
    document.getElementById('contato')?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (window.Cal?.loaded) {
      window.Cal('inline', {
        elementOrSelector: '#cal-inline',
        calLink: 'eliane-forti-fernandes',
        layout: 'month_view',
      });
      return;
    }
    const script = document.createElement('script');
    script.id = 'cal-init-script';
    script.textContent = `
      (function (C, A, L) {
        let p = function (a, ar) { a.q.push(ar); };
        let d = C.document;
        C.Cal = C.Cal || function () {
          let cal = C.Cal; let ar = arguments;
          if (!cal.loaded) {
            cal.ns = {}; cal.q = cal.q || [];
            d.head.appendChild(d.createElement("script")).src = A;
            cal.loaded = true;
          }
          if (ar[0] === L) {
            const api = function () { p(api, arguments); };
            const namespace = ar[1]; api.q = api.q || [];
            if (typeof namespace === "string") {
              cal.ns[namespace] = cal.ns[namespace] || api;
              p(cal.ns[namespace], ar);
            } else {
              p(cal, ar);
            }
            return;
          }
          p(cal, ar);
        };
      })(window, "https://app.cal.com/embed/embed.js", "init");
      Cal("init", { origin: "https://cal.com" });
      Cal("inline", {
        elementOrSelector: "#cal-inline",
        calLink: "eliane-forti-fernandes",
        layout: "month_view"
      });
      Cal("ui", {
        theme: "light",
        styles: { branding: { brandColor: "#2a3c5c" } },
        hideEventTypeDetails: false,
        layout: "month_view"
      });
    `;
    document.body.appendChild(script);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors = validateForm(formData);
    const hasErrors = Object.values(nextErrors).some(Boolean);

    if (hasErrors) {
      setFormErrors(nextErrors);
      setFormStatus('error');
      return;
    }

    setFormStatus('sending');
    setFormErrors({
      name: '',
      whatsapp: '',
      email: '',
      message: '',
    });

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      let data;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        data = await response.json();
      } else {
        const text = await response.text();
        console.error('Resposta não-JSON:', text);
        throw new Error('Resposta inválida do servidor');
      }

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Erro ao enviar mensagem');
      }

      setFormStatus('success');
      setFormData({ name: '', email: '', message: '', whatsapp: '' });

      // Resetar o status após 5 segundos
      setTimeout(() => {
        setFormStatus('idle');
      }, 5000);
    } catch (error) {
      console.error('Erro no envio:', error);
      setFormStatus('error');
      // Resetar o status de erro após 5 segundos
      setTimeout(() => {
        setFormStatus('idle');
      }, 5000);
    }
  };

  // Vindo de outra página por /#secao (menu dentro de um post): rola até a seção depois de montar
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return;
    // 'instant': com scroll-behavior smooth no CSS, a rolagem suave era cancelada durante o carregamento
    const timer = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'instant' as ScrollBehavior }), 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    // Aplicar imediatamente
    handleResize();
    handleScroll();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    
    // Forçar um reflow/repaint inicial para garantir que o cabeçalho seja renderizado corretamente
    setTimeout(() => {
      window.dispatchEvent(new Event('scroll'));
      window.dispatchEvent(new Event('resize'));
    }, 100);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden w-full">
      <SEO />
      <MainNavigation
        isMobile={isMobile}
        scrollPosition={scrollPosition}
        logoImage={logoImage}
        blogImage={blogImage}
        handleScheduleClick={handleScheduleClick}
      />

      <WhatsAppButton />

      <HeroSection
        heroImageDesktop={heroImageDesktop}
        heroImageMobile={heroImageMobile}
        handleScheduleClick={handleScheduleClick}
      />

      <ServicesSection />

      <AboutSection 
        sobreImage={sobreImage}
      />

      <AboutExtrasSection 
        pecaImage={pecaImage}
      />

      <TestimonialsSection />

      <CtaSection handleScheduleClick={handleScheduleClick} />

      {/* FAQ Section */}
      <motion.section
        className="py-14 sm:py-16 md:py-20 bg-[#f3f6fb]"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <Helmet>
          <script type="application/ld+json">{JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'Como funciona a psicoterapia?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'A psicoterapia é um processo de escuta e elaboração. Em cada sessão, você terá um espaço ético e sigiloso para falar sobre suas vivências, sentimentos e conflitos, construindo novas formas de compreender sua história e lidar com o que hoje gera sofrimento.',
                },
              },
              {
                '@type': 'Question',
                name: 'Qual é o valor da sessão?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'O valor é informado no primeiro contato, de forma transparente. Você pode enviar mensagem pelo WhatsApp para receber as informações e verificar disponibilidade de horários.',
                },
              },
              {
                '@type': 'Question',
                name: 'Com que frequência acontecem as sessões?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'A frequência mais comum é semanal, pois favorece continuidade e profundidade no processo terapêutico. A definição final é feita de acordo com sua necessidade clínica e sua disponibilidade.',
                },
              },
              {
                '@type': 'Question',
                name: 'Como é a primeira sessão?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'A primeira sessão é um momento de acolhimento e escuta inicial. Você poderá compartilhar o que motivou a busca por terapia, tirar dúvidas e entender como será o trabalho, sem pressão e no seu tempo.',
                },
              },
              {
                '@type': 'Question',
                name: 'A psicóloga atende online e presencial?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Sim. O atendimento pode ser online ou presencial. No presencial, o consultório fica na Av. Dr. Olindo Dartora, 205 - SALA 5 - Jardim Santo Antonio, Caieiras - SP, 07700-230. As duas modalidades seguem os mesmos princípios éticos e técnicos.',
                },
              },
              {
                '@type': 'Question',
                name: 'Posso remarcar ou cancelar uma sessão?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Sim. Remarcações e cancelamentos podem ser feitos com antecedência mínima de 6 horas. Em caso de faltas sem aviso ou cancelamentos fora desse prazo, pode haver cobrança da sessão.',
                },
              },
            ],
          })}</script>
        </Helmet>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex rounded-full bg-white px-4 py-2 text-xs tracking-[0.16em] uppercase text-[#2a3c5c] font-medium border border-[#d7deeb]">
              FAQ
            </span>
          </div>
          <motion.h2
            className="text-3xl sm:text-4xl font-semibold text-center mb-4 text-[#2a3c5c]"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Dúvidas comuns antes de iniciar
          </motion.h2>
          <p className="text-gray-600 text-center text-lg max-w-3xl mx-auto mb-10">
            Respostas diretas para facilitar sua decisão e ajudar você a dar o primeiro passo com tranquilidade.
          </p>
          <div className="space-y-4">
            {[
              {
                question: "Como funciona a psicoterapia?",
                answer: "A psicoterapia é um processo de escuta e elaboração. Em cada sessão, você terá um espaço ético e sigiloso para falar sobre suas vivências, sentimentos e conflitos, construindo novas formas de compreender sua história e lidar com o que hoje gera sofrimento."
              },
              {
                question: "Qual é o valor da sessão?",
                answer: "O valor é informado no primeiro contato, de forma transparente. Caso queira, você pode enviar mensagem pelo WhatsApp para receber as informações e verificar disponibilidade de horários."
              },
              {
                question: "Com que frequência acontecem as sessões?",
                answer: "A frequência mais comum é semanal, pois favorece continuidade e profundidade no processo terapêutico. A definição final é feita de acordo com sua necessidade clínica e sua disponibilidade."
              },
              {
                question: "Como é a primeira sessão?",
                answer: "A primeira sessão é um momento de acolhimento e escuta inicial. Você poderá compartilhar o que motivou a busca por terapia, tirar dúvidas e entender como será o trabalho, sem pressão e no seu tempo."
              },
              {
                question: "Atende online e presencial?",
                answer: "Sim. O atendimento pode ser online ou presencial. No presencial, o consultório fica na Av. Dr. Olindo Dartora, 205 - SALA 5 - Jardim Santo Antonio, Caieiras - SP, 07700-230. As duas modalidades seguem os mesmos princípios éticos e técnicos e podem ser escolhidas conforme sua rotina e preferência."
              },
              {
                question: "Posso remarcar ou cancelar uma sessão?",
                answer: "Sim. Remarcações e cancelamentos podem ser feitos com antecedência mínima de 6 horas. Em caso de faltas sem aviso ou cancelamentos fora desse prazo, pode haver cobrança da sessão."
              }
            ].map((faq, index) => {
              const isOpen = openFaqIndex === index;

              return (
                <motion.div 
                  key={index}
                  className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <motion.button
                    className={`w-full flex justify-between items-center p-6 text-left focus:outline-none ${isOpen ? 'bg-gradient-to-r from-[#2a3c5c]/10 to-[#4a6c9c]/10' : 'bg-white'}`}
                    onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                    whileHover={{ backgroundColor: '#f9fafb' }}
                  >
                    <h3 className="text-lg sm:text-xl font-semibold text-[#2a3c5c] pr-8">
                      {faq.question}
                    </h3>
                    <motion.div
                      animate={{ rotate: isOpen ? 45 : 0 }}
                      transition={{ duration: 0.3 }}
                      className={`flex items-center justify-center h-8 w-8 rounded-full border ${isOpen ? 'border-[#2a3c5c] text-[#2a3c5c]' : 'border-gray-300 text-gray-400'}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    </motion.div>
                  </motion.button>
                  
                  <motion.div
                    initial={false}
                    animate={{
                      height: isOpen ? 'auto' : 0,
                      opacity: isOpen ? 1 : 0
                    }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="p-6 pt-0 border-t border-gray-100">
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <a
              href="#agendamento"
              className="inline-flex items-center justify-center rounded-lg bg-[#2a3c5c] px-7 py-3 text-white font-semibold transition duration-300 hover:bg-[#1f2f4c]"
            >
              Tirar dúvidas e agendar
            </a>
          </div>
        </div>
      </motion.section>

      {/* Blog Highlight Section */}
      <motion.section
        id="blog"
        className="py-16 bg-white relative overflow-hidden"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        {/* Background decorations */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-[#2a3c5c]/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#4a6c9c]/5 rounded-full translate-x-1/3 translate-y-1/3"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-12">
            <motion.h2 
              className="text-2xl sm:text-3xl font-bold mb-4 text-[#2a3c5c]"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              Conheça Nosso Blog
            </motion.h2>
            <motion.p 
              className="text-gray-600 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Artigos e reflexões sobre psicologia, saúde mental e desenvolvimento emocional para te ajudar a viver melhor.
            </motion.p>
          </div>
          
          <div className="py-8">
            <BlogCarousel posts={blogPosts} />
          </div>
          
          <div className="mt-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              viewport={{ once: true }}
            >
              <a
                href="/blog"
                className="inline-flex items-center justify-center bg-gradient-to-r from-[#2a3c5c] to-[#4a6c9c] text-white px-6 py-3 rounded-lg hover:from-[#2a3c5c] hover:to-[#2a3c5c] transition-all duration-300 shadow-md hover:shadow-lg"
              >
                Explorar todos os artigos
              </a>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Seção unificada: Agendamento + Contato */}
      <section id="contato" className="py-20 bg-[#f3f6fb]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex rounded-full bg-white px-4 py-2 text-xs tracking-[0.16em] uppercase text-[#2a3c5c] font-medium border border-[#d7deeb] mb-5">
              Primeiro passo
            </span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#2a3c5c] mb-4">
              Agende sua consulta ou tire suas dúvidas
            </h2>
            <p className="text-gray-600 text-lg max-w-xl mx-auto">
              Escolha a forma que preferir para dar o primeiro passo.
            </p>
          </motion.div>

          {/* Tabs */}
          <div className="flex gap-1 mb-8 bg-white rounded-xl p-1 border border-[#d7deeb] max-w-sm mx-auto shadow-sm">
            <button
              onClick={() => setContactTab('agendar')}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all duration-200 ${
                contactTab === 'agendar'
                  ? 'bg-[#2a3c5c] text-white shadow'
                  : 'text-[#2a3c5c] hover:bg-[#edf2fb]'
              }`}
            >
              Agendar online
            </button>
            <button
              onClick={() => setContactTab('contato')}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all duration-200 ${
                contactTab === 'contato'
                  ? 'bg-[#2a3c5c] text-white shadow'
                  : 'text-[#2a3c5c] hover:bg-[#edf2fb]'
              }`}
            >
              Tenho uma dúvida
            </button>
          </div>

          {/* Tab: Cal.com — mantido no DOM para inicializar corretamente */}
          <motion.div
            id="agendamento"
            className={contactTab === 'agendar' ? 'block' : 'hidden'}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <div id="cal-inline" style={{ width: '100%', height: '700px' }} />
          </motion.div>

          {/* Tab: Formulário de contato */}
          <div className={contactTab === 'contato' ? 'block' : 'hidden'}>
            <motion.div
              className="grid md:grid-cols-2 gap-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div>
                <h3 className="text-2xl font-semibold text-[#2a3c5c] mb-4">Tem alguma dúvida antes de agendar?</h3>
                <p className="text-gray-600 mb-8">
                  Preencha o formulário e eu retorno em até 24h úteis para conversarmos sobre sua necessidade, formato de atendimento e próximos passos.
                </p>
                <div className="rounded-2xl border border-[#d7deeb] bg-white p-5 mb-8">
                  <p className="text-[#2a3c5c] font-semibold mb-2">Sigilo e ética profissional</p>
                  <ul className="text-gray-600 space-y-1 text-sm">
                    <li>Retorno em até 24h úteis</li>
                    <li>Presencial em Caieiras e online</li>
                    <li>Dados tratados com confidencialidade</li>
                  </ul>
                </div>
                <div className="space-y-4 text-sm">
                  <div className="flex items-center gap-3">
                    <MapPin className="text-[#2a3c5c] w-5 h-5 shrink-0" />
                    <a
                      href="https://www.google.com/maps/search/?api=1&query=Av.+Dr.+Olindo+Dartora%2C+205+-+SALA+5+-+Jardim+Santo+Antonio%2C+Caieiras+-+SP%2C+07700-230"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-[#2a3c5c] hover:underline"
                    >
                      Av. Dr. Olindo Dartora, 205 - SALA 5 - Jardim Santo Antonio, Caieiras - SP
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="text-[#2a3c5c] w-5 h-5 shrink-0" />
                    <a href="mailto:contato@efortipsi.com.br" className="text-gray-600 hover:text-[#2a3c5c]">
                      contato@efortipsi.com.br
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="text-[#2a3c5c] w-5 h-5 shrink-0" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                    </svg>
                    <a href={whatsappHref(WHATSAPP_CELULAR)} onClick={() => registrarCliqueWhatsApp('contato_celular')} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#2a3c5c]">
                      (11) 98476-3658
                    </a>
                  </div>
                </div>
              </div>

              <div>
                <form onSubmit={handleSubmit} className="space-y-5 rounded-2xl border border-[#d7deeb] bg-white p-6 sm:p-8 shadow-[0_12px_35px_rgba(26,43,73,0.08)]">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Nome</label>
                    <input
                      type="text" id="name" name="name"
                      value={formData.name} onChange={handleInputChange} required
                      aria-invalid={Boolean(formErrors.name)}
                      className={`w-full px-4 py-3 rounded-lg border focus:border-[#2a3c5c] focus:ring-2 focus:ring-[#2a3c5c]/20 outline-none transition duration-200 ${formErrors.name ? 'border-red-400 bg-red-50/40' : 'border-[#cfd8ea]'}`}
                      placeholder="Seu nome completo"
                    />
                    {formErrors.name && <p className="mt-2 text-sm text-red-600">{formErrors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="whatsapp" className="block text-sm font-medium text-gray-700 mb-2">WhatsApp</label>
                    <input
                      type="tel" id="whatsapp" name="whatsapp"
                      value={formData.whatsapp} onChange={handleInputChange} required
                      inputMode="numeric" aria-invalid={Boolean(formErrors.whatsapp)}
                      className={`w-full px-4 py-3 rounded-lg border focus:border-[#2a3c5c] focus:ring-2 focus:ring-[#2a3c5c]/20 outline-none transition duration-200 ${formErrors.whatsapp ? 'border-red-400 bg-red-50/40' : 'border-[#cfd8ea]'}`}
                      placeholder="Seu número com DDD" maxLength={15}
                    />
                    {formErrors.whatsapp && <p className="mt-2 text-sm text-red-600">{formErrors.whatsapp}</p>}
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">E-mail</label>
                    <input
                      type="email" id="email" name="email"
                      value={formData.email} onChange={handleInputChange} required
                      aria-invalid={Boolean(formErrors.email)}
                      className={`w-full px-4 py-3 rounded-lg border focus:border-[#2a3c5c] focus:ring-2 focus:ring-[#2a3c5c]/20 outline-none transition duration-200 ${formErrors.email ? 'border-red-400 bg-red-50/40' : 'border-[#cfd8ea]'}`}
                      placeholder="seu@email.com"
                    />
                    {formErrors.email && <p className="mt-2 text-sm text-red-600">{formErrors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">O que você deseja cuidar neste momento?</label>
                    <textarea
                      id="message" name="message"
                      value={formData.message} onChange={handleInputChange} required rows={4}
                      aria-invalid={Boolean(formErrors.message)}
                      className={`w-full px-4 py-3 rounded-lg border focus:border-[#2a3c5c] focus:ring-2 focus:ring-[#2a3c5c]/20 outline-none transition duration-200 resize-none ${formErrors.message ? 'border-red-400 bg-red-50/40' : 'border-[#cfd8ea]'}`}
                      placeholder="Ex.: ansiedade, conflitos nos relacionamentos, sensação de sobrecarga..."
                    />
                    {formErrors.message && <p className="mt-2 text-sm text-red-600">{formErrors.message}</p>}
                  </div>
                  <p className="text-sm text-gray-500">Ao enviar, seus dados serão usados apenas para contato sobre atendimento psicológico.</p>
                  <button
                    type="submit"
                    disabled={formStatus === 'sending'}
                    className={`w-full font-semibold px-8 py-4 rounded-lg transition duration-300 ${formStatus === 'sending' ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#2a3c5c] text-white hover:bg-[#3a4c6c]'}`}
                  >
                    {formStatus === 'sending' ? 'Enviando...' : 'Enviar mensagem'}
                  </button>
                  {formStatus === 'success' && (
                    <div className="p-4 bg-green-100 text-green-700 rounded-lg">
                      Mensagem enviada! Retorno em breve.
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="p-4 bg-red-100 text-red-700 rounded-lg">
                      Revise os campos destacados ou tente novamente em instantes.
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setContactTab('agendar')}
                    className="w-full text-sm text-[#2a3c5c] font-medium hover:underline"
                  >
                    Prefere agendar diretamente? Ver calendário →
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

// Conteúdo do app sem o Router: o navegador usa BrowserRouter (App) e o pré-render usa StaticRouter (entry-server)
export function AppContent() {
  const [showCookieConsent, setShowCookieConsent] = useState(() => typeof window !== 'undefined' && localStorage.getItem(COOKIE_CONSENT_KEY) === null);

  const handleCookieConsent = (accepted: boolean) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, accepted ? 'accepted' : 'rejected');
    setShowCookieConsent(false);

    if (!import.meta.env.PROD) {
      return;
    }

    if (accepted) {
      loadGtm();
      updateConsentMode(true);
      return;
    }

    if (window.__gtmLoaded) {
      updateConsentMode(false);
    }
  };

  useEffect(() => {
    let viewportMeta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
    
    if (!viewportMeta) {
      viewportMeta = document.createElement('meta');
      viewportMeta.setAttribute('name', 'viewport');
      document.head.appendChild(viewportMeta);
    }
    
    viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0');

    if (!import.meta.env.PROD) {
      return;
    }

    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);

    if (consent === 'accepted') {
      loadGtm();
      updateConsentMode(true);
      return;
    }

    if (consent === 'rejected' && window.__gtmLoaded) {
      updateConsentMode(false);
    }
  }, []);

  return (
    <>
      <CookieConsentBanner
        showCookieConsent={showCookieConsent}
        handleCookieConsent={handleCookieConsent}
      />
      <AppRoutes />
    </>
  );
}

// Componente App principal
function App() {
  return (
    <HelmetProvider>
      <Router>
        <AppContent />
      </Router>
      <Analytics mode={import.meta.env.PROD ? 'production' : 'development'} />
    </HelmetProvider>
  );
}

export default App;
